import React, { useEffect, useRef } from 'react';
import { fmtClock } from '../myDayUtils';

/**
 * Pomodoro dock. It counts down in real time and reports elapsed seconds back
 * so the parent can bank focus time against the task even if the session is
 * abandoned half way.
 */
export default function FocusTimer({ session, task, settings, onTick, onChange, onStop, onComplete }) {
  const beepRef = useRef(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!session?.running) return undefined;
    const id = setInterval(() => onTick(), 1000);
    return () => clearInterval(id);
  }, [session?.running, onTick]);

  useEffect(() => {
    if (!session || session.remaining > 0) { firedRef.current = false; return; }
    if (firedRef.current) return;
    firedRef.current = true;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        const ctx = beepRef.current || (beepRef.current = new Ctx());
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
        osc.start(); osc.stop(ctx.currentTime + 0.6);
      }
    } catch { /* audio is a nicety, never a failure */ }
    onComplete();
  }, [session, onComplete]);

  if (!session) return null;
  const total = Math.max(1, session.totalSec);
  const pct = Math.max(0, Math.min(100, ((total - session.remaining) / total) * 100));
  const isBreak = session.mode === 'break';

  return (
    <div className={`md-focus${isBreak ? ' md-focus--break' : ''}`} role="region" aria-label="Focus timer">
      <div className="md-focus__bar" aria-hidden="true"><span style={{ width: `${pct}%` }} /></div>
      <div className="md-focus__body">
        <span className="md-focus__mode">{isBreak ? '☕ Break' : '🎯 Focus'}</span>
        <span className="md-focus__clock" aria-live="off">{fmtClock(session.remaining)}</span>
        <span className="md-focus__task" title={task?.title}>{task?.title || 'Unassigned session'}</span>
        <span className="md-focus__actions">
          <button className="md-btn md-btn--sm" onClick={() => onChange({ running: !session.running })}>
            {session.running ? '⏸ Pause' : '▶ Resume'}
          </button>
          <button className="md-btn md-btn--sm" onClick={() => onChange({
            remaining: (isBreak ? settings.breakMin : settings.pomodoroMin) * 60,
            totalSec: (isBreak ? settings.breakMin : settings.pomodoroMin) * 60,
          })}>↺ Restart</button>
          <button className="md-btn md-btn--sm md-btn--danger" onClick={onStop}>✕ End</button>
        </span>
      </div>
    </div>
  );
}
